import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import './AssessmentSubmission.css';

export default function StudentUpload() {

  // ── Identity ──────────────────────────────────────────────
  // const orgCode = 
  const orgCode = localStorage.getItem("orgCode");
  // const hardcodedStudentId = 
  const hardcodedStudentId = localStorage.getItem("studentId");

  const [resolvedOrgCode, setResolvedOrgCode] = useState('');
  const [resolvedStudentId, setResolvedStudentId] = useState('');
  const [studentName, setStudentName] = useState('');

  // ── Form state ────────────────────────────────────────────
  const [description, setDescription] = useState('');
  const [driveLink, setDriveLink] = useState('');

  // ── Course + Module cascade ───────────────────────────────
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState('');
  const [modulesLoading, setModulesLoading] = useState(false);

  // ── Edit mode ─────────────────────────────────────────────
  // When editing, holds the submission being edited (so we know the doc ID)
  const [editingSubmission, setEditingSubmission] = useState(null);
  // After module list loads for editing, we need to restore selectedModule
  const pendingModuleId = useRef(null);

  // ── Existing submission preview (auto-fill) ───────────────
  const [submissionPreview, setSubmissionPreview] = useState(null); // existing sub for current course+module

  // ── UI state ──────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [loadingStudent, setLoadingStudent] = useState(true);
  const [existingSubmissions, setExistingSubmissions] = useState([]);

  // ── Ref for scrolling to form ─────────────────────────────
  const formRef = useRef(null);

  // ── Resolve identity ──────────────────────────────────────
  useEffect(() => {
    setResolvedOrgCode(orgCode);
    setResolvedStudentId(hardcodedStudentId);
  }, []);

  // ── Fetch student name + courses + submissions ────────────
  useEffect(() => {
    if (!resolvedOrgCode || !resolvedStudentId) { setLoadingStudent(false); return; }
    const init = async () => {
      setLoadingStudent(true);
      try {
        const studentRef = doc(db, `CorporateClients/${resolvedOrgCode}/studentInfo/${resolvedStudentId}`);
        const snap = await getDoc(studentRef);
        setStudentName(snap.exists() ? (snap.data().fullName || resolvedStudentId) : resolvedStudentId);

        try {
          const progRef = collection(db, `CorporateClients/${resolvedOrgCode}/programInfo`);
          const progSnap = await getDocs(progRef);
          const cats = [];
          progSnap.forEach(d => cats.push(d.id));
          cats.sort();
          setCategories(cats);
          if (cats.length > 0) setCategory(cats[0]);
        } catch (_) {}

        await refreshSubmissions(resolvedOrgCode, resolvedStudentId);
      } catch (err) {
        console.error('Error loading student data:', err);
      } finally {
        setLoadingStudent(false);
      }
    };
    init();
  }, [resolvedOrgCode, resolvedStudentId]);

  // ── Fetch modules when course changes ────────────────────
  useEffect(() => {
    if (!resolvedOrgCode || !category) { setModules([]); setSelectedModule(''); return; }
    const fetchModules = async () => {
      setModulesLoading(true);
      setSelectedModule('');
      setModules([]);
      try {
        const modRef = collection(db, `CorporateClients/${resolvedOrgCode}/programInfo/${category}/modules`);
        const modSnap = await getDocs(modRef);
        const mods = [];
        modSnap.forEach(d => {
          const data = d.data();
          mods.push({ id: d.id, name: data.name || data.moduleName || d.id });
        });
        mods.sort((a, b) => a.name.localeCompare(b.name));
        setModules(mods);

        // If we're restoring a module for edit mode, use pendingModuleId
        if (pendingModuleId.current) {
          setSelectedModule(pendingModuleId.current);
          pendingModuleId.current = null;
        } else if (mods.length > 0) {
          setSelectedModule(mods[0].id);
        }
      } catch (err) {
        console.error('Error fetching modules:', err);
      } finally {
        setModulesLoading(false);
      }
    };
    fetchModules();
  }, [resolvedOrgCode, category]);

  // ── Auto-fill form when course + module selected ──────────
  useEffect(() => {
    // Don't auto-fill while in edit-icon edit mode (that path handles it)
    if (editingSubmission) return;
    if (!category || !selectedModule) {
      setDriveLink('');
      setDescription('');
      setSubmissionPreview(null);
      return;
    }
    const key = `${category}_${selectedModule}`;
    const existing = existingSubmissions.find(s => s.id === key);
    if (existing) {
      setDriveLink(existing.driveLink || '');
      setDescription(existing.description || '');
      setSubmissionPreview(existing);
    } else {
      setDriveLink('');
      setDescription('');
      setSubmissionPreview(null);
    }
  }, [category, selectedModule, editingSubmission]);

  // ── Helpers ───────────────────────────────────────────────
  const refreshSubmissions = async (org, sid) => {
    try {
      const subRef = collection(db, `CorporateClients/${org}/studentInfo/${sid}/submissions`);
      const subSnap = await getDocs(subRef);
      const subs = [];
      subSnap.forEach(d => subs.push({ id: d.id, ...d.data() }));
      subs.sort((a, b) => (b.submittedAt?.seconds || 0) - (a.submittedAt?.seconds || 0));
      setExistingSubmissions(subs);
    } catch (_) {}
  };

  const isValidDriveLink = (url) =>
    /drive\.google\.com/i.test(url) || /docs\.google\.com/i.test(url);

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // ── Click edit icon → fill form ───────────────────────────
  const handleEditClick = (sub) => {
    setEditingSubmission(sub);
    setDriveLink(sub.driveLink || '');
    setDescription(sub.description || '');
    setSuccessMsg('');
    setErrorMsg('');

    // Set course — module list will load via useEffect, then pendingModuleId restores it
    pendingModuleId.current = sub.moduleId;
    setCategory(sub.course || '');

    // Scroll to form
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Cancel edit ───────────────────────────────────────────
  const handleCancelEdit = () => {
    setEditingSubmission(null);
    setDriveLink('');
    setDescription('');
    setSuccessMsg('');
    setErrorMsg('');
    pendingModuleId.current = null;
    if (categories.length > 0) setCategory(categories[0]);
  };

  // ── Submit / Update ───────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMsg('');
    setErrorMsg('');
    if (!resolvedStudentId) { setErrorMsg('Student ID not found.'); return; }
    if (!category) { setErrorMsg('Please select a course.'); return; }
    if (!selectedModule) { setErrorMsg('Please select a module.'); return; }
    if (!driveLink.trim()) { setErrorMsg('Please enter a Google Drive link.'); return; }
    if (!isValidDriveLink(driveLink.trim())) {
      setErrorMsg('Please enter a valid Google Drive or Google Docs link.');
      return;
    }

    setSubmitting(true);
    try {
      const submissionId = `${category}_${selectedModule}`;
      const submissionRef = doc(db, `CorporateClients/${resolvedOrgCode}/studentInfo/${resolvedStudentId}/submissions/${submissionId}`);
      const moduleObj = modules.find(m => m.id === selectedModule);

      const payload = {
        studentId: resolvedStudentId,
        studentName: studentName || resolvedStudentId,
        orgCode: resolvedOrgCode,
        course: category,
        moduleId: selectedModule,
        moduleName: moduleObj?.name || selectedModule,
        description: description.trim(),
        driveLink: driveLink.trim(),
        status: 'uploaded',
      };

      if (editingSubmission) {
        // Update: keep original submittedAt, add updatedAt
        await updateDoc(submissionRef, { ...payload, updatedAt: serverTimestamp() });
        setSuccessMsg(`"${moduleObj?.name || selectedModule}" updated successfully ✓`);
      } else {
        // New submission
        await setDoc(submissionRef, { ...payload, submittedAt: serverTimestamp() });
        setSuccessMsg(`"${moduleObj?.name || selectedModule}" submitted successfully ✓`);
      }

      await refreshSubmissions(resolvedOrgCode, resolvedStudentId);
      setEditingSubmission(null);
      setDriveLink('');
      setDescription('');
      if (categories.length > 0) setCategory(categories[0]);
    } catch (err) {
      console.error('Upload error:', err);
      setErrorMsg('Failed: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="su-page">
      {loadingStudent ? (
        <div className="su-loading">Loading your profile…</div>
      ) : (
        <div className="su-wrapper">

          {/* ══ TOP: Submit / Edit form ════════════════════════ */}
          <div className="su-card" ref={formRef}>
            <div className={`su-card-title-bar ${editingSubmission ? 'su-card-title-bar--edit' : ''}`}>
              <span className="su-card-heading">
                {editingSubmission
                  ? `✏ Editing: ${editingSubmission.moduleName || editingSubmission.moduleId}`
                  : 'Submit Assignment PDF'}
              </span>
              {editingSubmission && (
                <button className="su-cancel-edit-btn" onClick={handleCancelEdit}>
                  ✕ Cancel Edit
                </button>
              )}
            </div>

            <form className="su-form" onSubmit={handleSubmit} noValidate>

              {/* Course */}
              <div className="su-row">
                <label className="su-label" htmlFor="su-category">
                  Course <span className="su-req">*</span>
                </label>
                {categories.length > 0 ? (
                  <select
                    id="su-category"
                    className="su-select"
                    value={category}
                    onChange={e => { setCategory(e.target.value); }}
                  >
                    <option value="">— Select a course —</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                ) : (
                  <p className="su-hint-txt">No courses found.</p>
                )}
              </div>

              {/* Module */}
              {category && (
                <div className="su-row">
                  <label className="su-label" htmlFor="su-module">
                    Module <span className="su-req">*</span>
                  </label>
                  {modulesLoading ? (
                    <p className="su-hint-txt su-loading-inline">Loading modules…</p>
                  ) : modules.length > 0 ? (
                    <select
                      id="su-module"
                      className="su-select"
                      value={selectedModule}
                      onChange={e => setSelectedModule(e.target.value)}
                    >
                      <option value="">— Select a module —</option>
                      {modules.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                    </select>
                  ) : (
                    <p className="su-hint-txt">No modules found for <strong>{category}</strong>.</p>
                  )}
                </div>
              )}

              {/* Already-submitted banner */}
              {submissionPreview && !editingSubmission && (
                <div className="su-preview-banner">
                  <span className="su-preview-icon">📄</span>
                  <span>
                    Already submitted for <strong>{submissionPreview.moduleName || submissionPreview.moduleId}</strong>.
                    The link below is pre-filled — update it or just click <strong>Submit</strong> to resubmit.
                  </span>
                </div>
              )}

              {/* Drive link */}
              <div className="su-label-row">
                <label className="su-label" htmlFor="su-drive-link">
                  Google Drive PDF Link <span className="su-req">*</span>
                </label>
                      <a
                    href="https://drive.google.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="su-drive-open-btn"
                    title="Open Google Drive to upload your PDF"
                  >
                    <svg viewBox="0 0 24 24" fill="none" className="su-drive-btn-icon">
                      <path d="M12 2L3 7l9 5 9-5-9-5z" fill="#4285F4"/>
                      <path d="M3 7v10l9 5V12L3 7z" fill="#34A853"/>
                      <path d="M21 7v10l-9 5V12l9-5z" fill="#FBBC04"/>
                    </svg>
                    Open Google Drive
                  </a>
                <input
                  id="su-drive-link"
                  type="url"
                  className="su-input"
                  placeholder="https://drive.google.com/file/d/..."
                  value={driveLink}
                  onChange={e => setDriveLink(e.target.value)}
                />
                <p className="su-hint-txt">Upload the file to Google Drive and share the link here..</p>
              </div>


              {/* Notes */}
              <div className="su-row">
                <label className="su-label" htmlFor="su-description">
                  Notes <span className="su-opt">(optional)</span>
                </label>
                <textarea
                  id="su-description"
                  className="su-textarea"
                  placeholder="Briefly describe this submission…"
                  rows={3}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                />
              </div>

              {successMsg && <div className="su-alert su-alert--success">{successMsg}</div>}
              {errorMsg   && <div className="su-alert su-alert--error">{errorMsg}</div>}

              <div className="su-form-footer">
                <button
                  type="submit"
                  className={`su-btn ${editingSubmission ? 'su-btn--update' : ''}`}
                  disabled={submitting || !resolvedStudentId || !category || !selectedModule}
                >
                  {submitting
                    ? (editingSubmission ? 'Updating…' : 'Uploading…')
                    : (editingSubmission ? 'Update Submission' : 'Submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

